import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import {
  X,
  HelpCircle,
  Barcode as BarcodeIcon,
  Images,
  Flashlight,
  UtensilsCrossed,
  Camera,
} from "lucide-react";
import { barcodeApiService } from "../../services/barcodeApiService";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import { useFoodManagement } from "../../hooks/useFoodManagement";
import { useMealPhotoAnalyze } from "../../hooks/useMealPhotoAnalyze";
import BarcodeScanner from "../../components/BarcodeScanner";
import BestScanningPractices from "../../components/BestScanningPractices";
import MealAnalyzingProgressOverlay from "../../components/MealAnalyzingProgressOverlay";

type CaptureMode = "camera" | "gallery" | "barcode";

/** Same three choices as the old bottom-nav picker: Camera · Gallery · Barcode */
const modes: {
  id: CaptureMode;
  label: string;
  Icon: typeof Camera | typeof Images | typeof BarcodeIcon;
}[] = [
  { id: "camera", label: "Camera", Icon: Camera },
  { id: "gallery", label: "Gallery", Icon: Images },
  { id: "barcode", label: "Barcode", Icon: BarcodeIcon },
];

const MealCameraPage = () => {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const [mode, setMode] = useState<CaptureMode>("camera");
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [showBestPractices, setShowBestPractices] = useState(() => {
    if (typeof window === "undefined") return false;
    return !localStorage.getItem("glucera_seen_scanning_practices");
  });
  const [cameraReady, setCameraReady] = useState(false);

  const { setUploadedImage } = useFoodManagement();
  const { analyzeMealPhoto, isAnalyzing, uploadProgress } = useMealPhotoAnalyze();

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraReady(false);
    setTorchSupported(false);
    setTorchOn(false);
  }, []);

  const startCamera = useCallback(async () => {
    stopCamera();
    try {
      let stream: MediaStream | null = null;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
      } catch {
        stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });
      }
      streamRef.current = stream;
      const v = videoRef.current;
      if (v) {
        v.srcObject = stream;
        await v.play().catch(() => {});
      }
      setCameraReady(true);
      const track = stream.getVideoTracks()[0];
      const caps = track?.getCapabilities?.() as { torch?: boolean } | undefined;
      setTorchSupported(!!caps?.torch);
    } catch (e) {
      console.error(e);
      message.warning("Could not access camera. Check permissions.");
    }
  }, [stopCamera]);

  useEffect(() => {
    if (showBestPractices) return;
    if (mode === "camera") {
      startCamera();
    } else {
      stopCamera();
    }
    return () => {
      stopCamera();
    };
  }, [mode, showBestPractices, startCamera, stopCamera]);

  const toggleTorch = async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track || !torchSupported) return;
    try {
      const next = !torchOn;
      await track.applyConstraints({ advanced: [{ torch: next }] as any });
      setTorchOn(next);
    } catch {
      message.warning("Flash not available on this device.");
    }
  };

  const prepareFileFromBlob = (file: File) => {
    const blobUrl = URL.createObjectURL(file);
    setUploadedImage(blobUrl);
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      localStorage.setItem("glucera_uploaded_image", base64String);
    };
    reader.readAsDataURL(file);
    analyzeMealPhoto(file);
  };

  const captureFrame = () => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) {
      message.warning("Camera not ready yet.");
      return;
    }
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) {
      message.warning("Video not ready.");
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, w, h);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], "meal-capture.jpg", { type: "image/jpeg" });
        stopCamera();
        prepareFileFromBlob(file);
      },
      "image/jpeg",
      0.92
    );
  };

  const handleShutter = () => {
    if (isAnalyzing) return;
    if (mode === "gallery") {
      galleryInputRef.current?.click();
      return;
    }
    if (mode === "camera") {
      captureFrame();
    }
  };

  /** Open native picker on the same tap as the Gallery tab (required on many mobile browsers). */
  const openGalleryFromTab = useCallback(() => {
    if (isAnalyzing) return;
    setMode("gallery");
    galleryInputRef.current?.click();
  }, [isAnalyzing]);

  const handleGalleryFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file?.type.startsWith("image/")) {
      prepareFileFromBlob(file);
    }
  };

  const handleBarcodeScan = async (barcode: string) => {
    const loadingMessage = message.loading("Looking up product...", 0);
    try {
      const product = await barcodeApiService.lookupProduct(barcode);
      loadingMessage();
      const foodData = {
        name: product.name,
        brand: product.brand,
        quantity: product.serving_size,
        image: product.image,
        barcode: product.barcode,
        calories: product.calories,
        protein: product.protein,
        carbs: product.carbs,
        net_carbs: product.net_carbs,
        fat: product.fat,
        sugar: product.sugar,
        fiber: product.fiber,
        saturated_fat: product.saturated_fat,
        trans_fat: product.trans_fat,
        sodium: product.sodium,
        cholesterol: product.cholesterol,
        gi_estimate: product.gi_estimate,
        gl_estimate: product.gl_estimate,
        alcohol: 0,
        caffeine: 0,
        meal_type: product.meal_type,
      };
      navigate("/glucera" + GluceraNavigationRoutes.SCANNED_BARCODE_DETAIL, {
        state: { foodData },
      });
    } catch (error: unknown) {
      loadingMessage();
      const msg = error instanceof Error ? error.message : "Failed to lookup product";
      message.error(msg);
    }
  };

  const handleClose = () => {
    stopCamera();
    navigate(-1);
  };

  const onBestScanNow = () => {
    localStorage.setItem("glucera_seen_scanning_practices", "true");
    setShowBestPractices(false);
  };

  const onBestClose = () => {
    localStorage.setItem("glucera_seen_scanning_practices", "true");
    setShowBestPractices(false);
  };

  return (
    <div className="fixed inset-0 z-[120] flex flex-col bg-black">
      {showBestPractices && (
        <BestScanningPractices onClose={onBestClose} onScanNow={onBestScanNow} />
      )}

      {mode === "barcode" && (
        <BarcodeScanner
          onScan={handleBarcodeScan}
          onClose={() => setMode("camera")}
        />
      )}

      <header className="relative z-30 flex items-center justify-between px-4 pb-2 pt-[max(12px,env(safe-area-inset-top))]">
        <button
          type="button"
          onClick={handleClose}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-md transition-colors hover:bg-white/25"
          aria-label="Close"
        >
          <X className="h-5 w-5" strokeWidth={2.5} />
        </button>
        <div className="flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 backdrop-blur-md">
          <UtensilsCrossed className="h-5 w-5 text-white" strokeWidth={2.25} />
          <span className="text-callout font-bold tracking-tight text-white">Bite Balance</span>
        </div>
        <button
          type="button"
          onClick={() => navigate("/glucera" + GluceraNavigationRoutes.PROFILE_HELP)}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-md transition-colors hover:bg-white/25"
          aria-label="Help"
        >
          <HelpCircle className="h-5 w-5" strokeWidth={2.25} />
        </button>
      </header>

      {mode === "camera" && (
        <div className="relative flex-1 overflow-hidden">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="absolute inset-0 h-full w-full object-cover"
            onLoadedMetadata={(e) => {
              e.currentTarget.play().catch(() => {});
            }}
          />
          {!cameraReady && !showBestPractices && (
            <div className="absolute inset-0 flex items-center justify-center bg-neutral-900">
              <p className="text-subhead text-white/70">Starting camera…</p>
            </div>
          )}
        </div>
      )}

      {mode === "gallery" && (
        <div className="relative flex flex-1 flex-col items-center justify-center gap-3 bg-neutral-950 px-6">
          <Images className="h-14 w-14 text-white/40" strokeWidth={1.25} />
          <p className="text-center text-callout font-semibold text-white">Gallery</p>
          <p className="text-center text-subhead text-white/60">
            Tap Gallery again or the shutter to pick another photo
          </p>
        </div>
      )}

      {!isAnalyzing && mode !== "barcode" && (
        <div className="relative z-30 bg-gradient-to-t from-black via-black/95 to-transparent pb-[max(16px,env(safe-area-inset-bottom))] pt-6">
          <div className="scrollbar-hide mb-5 flex gap-2 overflow-x-auto px-4 pb-1">
            {modes.map(({ id, label, Icon }) => {
              const active = mode === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => {
                    if (id === "gallery") {
                      openGalleryFromTab();
                    } else {
                      setMode(id);
                    }
                  }}
                  className={`flex shrink-0 items-center gap-2 rounded-2xl border px-4 py-2.5 text-caption1 font-bold tracking-wide transition-all ${
                    active
                      ? "border-white bg-white text-neutral-900 shadow-lg shadow-black/40"
                      : "border-white/20 bg-black/50 text-white backdrop-blur-md hover:bg-black/65"
                  }`}
                >
                  <Icon className="h-4 w-4 shrink-0" strokeWidth={2.25} />
                  {label}
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-center gap-10 px-8">
            {mode === "camera" && (
              <button
                type="button"
                onClick={toggleTorch}
                disabled={!torchSupported || !cameraReady}
                className={`flex h-11 w-11 items-center justify-center rounded-full border border-white/25 backdrop-blur-md transition-all ${
                  torchSupported && cameraReady
                    ? torchOn
                      ? "bg-amber-400 text-neutral-900"
                      : "bg-white/15 text-white hover:bg-white/25"
                    : "cursor-not-allowed bg-white/5 text-white/35"
                }`}
                aria-label="Flash"
              >
                <Flashlight className="h-5 w-5" strokeWidth={2.25} />
              </button>
            )}
            {mode === "gallery" && <div className="w-11" aria-hidden />}

            <button
              type="button"
              onClick={handleShutter}
              disabled={
                isAnalyzing || (mode === "camera" && !cameraReady)
              }
              className="flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-full border-[5px] border-white/35 bg-white shadow-[0_8px_40px_rgba(0,0,0,0.45)] transition-transform active:scale-95 disabled:opacity-40"
              aria-label={
                mode === "gallery" ? "Choose from gallery" : "Capture photo"
              }
            >
              <span className="h-[58px] w-[58px] rounded-full bg-white ring-2 ring-black/10" />
            </button>

            {(mode === "camera" || mode === "gallery") && (
              <div className="w-11" aria-hidden />
            )}
          </div>
        </div>
      )}

      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={handleGalleryFile}
      />

      {isAnalyzing && (
        <MealAnalyzingProgressOverlay uploadProgress={uploadProgress} />
      )}
    </div>
  );
};

export default MealCameraPage;
